import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Paperclip, RefreshCw, Send, Upload } from "lucide-react";
import { toast } from "sonner";
import type {
  AdminServiceRequestListResponse,
  AdminServiceRequestResponse,
  ServiceRequestAssetResponse,
  ServiceRequestUpdateResponse,
} from "@/lib/api/generated";
import {
  ServiceRequestAssetType,
  ServiceRequestStatus,
  ServiceRequestUpdateType,
} from "@/lib/api/generated";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import {
  createAdminServiceAssetSignatureServer,
  createAdminServiceRequestUpdateServer,
  getAdminServiceRequestTimelineServer,
  getAdminServiceRequestsServer,
  updateAdminServiceRequestServer,
} from "@/server/admin";
import {
  SERVICE_ASSET_TYPE_LABELS,
  SERVICE_STATUS_LABELS,
  SERVICE_TYPE_LABELS,
  SERVICE_UPDATE_TYPE_LABELS,
} from "@/lib/service-marketplace/catalog";
import { extractApiErrorMessage } from "@/lib/api/error";
import { uploadToOSS } from "@/lib/oss-upload";
import { logError } from "@/lib/structured-log";

const PAGE_SIZE = 50;

export const Route = createFileRoute("/admin/service-requests")({
  loader: async () => {
    const list = (await getAdminServiceRequestsServer({
      data: { skip: 0, limit: PAGE_SIZE },
    })) as AdminServiceRequestListResponse;
    return { list };
  },
  component: AdminServiceRequestsPage,
});

function AdminServiceRequestsPage() {
  const { list } = Route.useLoaderData();
  const [requests, setRequests] = useState<AdminServiceRequestResponse[]>(list.items ?? []);
  const [selectedId, setSelectedId] = useState<string | null>(requests[0]?.id ?? null);
  const [timeline, setTimeline] = useState<ServiceRequestUpdateResponse[]>([]);
  const [isTimelineLoading, setIsTimelineLoading] = useState(false);
  const selected = requests.find((item) => item.id === selectedId) ?? null;

  async function refreshList(): Promise<void> {
    const next = (await getAdminServiceRequestsServer({
      data: { skip: 0, limit: PAGE_SIZE },
    })) as AdminServiceRequestListResponse;
    setRequests(next.items ?? []);
    if (!selectedId && next.items?.[0]) {
      setSelectedId(next.items[0].id);
    }
  }

  useEffect(() => {
    if (!selectedId) {
      setTimeline([]);
      return;
    }
    let isActive = true;
    setIsTimelineLoading(true);
    getAdminServiceRequestTimelineServer({ data: { requestId: selectedId } })
      .then((items) => {
        if (isActive) setTimeline(items as ServiceRequestUpdateResponse[]);
      })
      .catch((error: unknown) => {
        logError("admin.service_requests.timeline.load_failed", { error });
        toast.error("加载服务进展失败");
      })
      .finally(() => {
        if (isActive) setIsTimelineLoading(false);
      });
    return () => {
      isActive = false;
    };
  }, [selectedId]);

  async function handleAfterMutate(): Promise<void> {
    await refreshList();
    if (selectedId) {
      const items = (await getAdminServiceRequestTimelineServer({
        data: { requestId: selectedId },
      })) as ServiceRequestUpdateResponse[];
      setTimeline(items);
    }
  }

  return (
    <div className="grid h-full min-h-0 grid-cols-[390px_1fr] overflow-hidden">
      <aside className="flex min-h-0 flex-col border-r">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <div>
            <h2 className="text-sm font-semibold">服务申请</h2>
            <p className="text-xs text-muted-foreground">{requests.length} 条申请</p>
          </div>
          <Button type="button" size="sm" variant="outline" onClick={() => refreshList()}>
            <RefreshCw className="size-4" />
          </Button>
        </div>
        <div className="min-h-0 flex-1 overflow-auto">
          {requests.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-muted-foreground">
              暂无服务申请。
            </div>
          ) : (
            requests.map((request) => (
              <button
                key={request.id}
                type="button"
                className={`flex w-full flex-col gap-2 border-b px-4 py-3 text-left hover:bg-muted/50 ${
                  request.id === selectedId ? "bg-muted" : ""
                }`}
                onClick={() => setSelectedId(request.id)}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="line-clamp-1 text-sm font-medium">
                    {SERVICE_TYPE_LABELS[request.service_type]}
                  </span>
                  <Badge variant="secondary">{SERVICE_STATUS_LABELS[request.status]}</Badge>
                </div>
                <p className="line-clamp-2 text-xs leading-5 text-muted-foreground">
                  {request.description}
                </p>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{request.requester_email || request.requester_id}</span>
                  <span>{formatDateTime(request.updated_at)}</span>
                </div>
              </button>
            ))
          )}
        </div>
      </aside>

      <main className="min-h-0 overflow-auto">
        {selected ? (
          <div className="mx-auto flex max-w-5xl flex-col gap-6 p-6">
            <section className="space-y-4 border-b pb-5">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h1 className="text-xl font-semibold">
                    {SERVICE_TYPE_LABELS[selected.service_type]}
                  </h1>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {selected.workspace_name || selected.workspace_slug || selected.workspace_id}
                  </p>
                </div>
                <Badge>{SERVICE_STATUS_LABELS[selected.status]}</Badge>
              </div>
              <div className="grid gap-3 text-sm md:grid-cols-3">
                <Info label="提交人" value={selected.requester_email || selected.requester_id} />
                <Info label="提交时间" value={formatDateTime(selected.submitted_at)} />
                <Info label="更新时间" value={formatDateTime(selected.updated_at)} />
              </div>
              <div className="rounded-lg bg-muted p-4 text-sm leading-6">
                {selected.description}
              </div>
            </section>

            <RequestAdminForm request={selected} onSaved={handleAfterMutate} />
            <ProgressPublisher requestId={selected.id} onPublished={handleAfterMutate} />
            <Timeline timeline={timeline} isLoading={isTimelineLoading} />
          </div>
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
            请选择一条服务申请。
          </div>
        )}
      </main>
    </div>
  );
}

function RequestAdminForm({
  request,
  onSaved,
}: {
  request: AdminServiceRequestResponse;
  onSaved: () => Promise<void>;
}) {
  const [status, setStatus] = useState<ServiceRequestStatus>(request.status);
  const [adminNote, setAdminNote] = useState(request.admin_note ?? "");
  const [billingReferenceId, setBillingReferenceId] = useState(request.billing_reference_id ?? "");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setStatus(request.status);
    setAdminNote(request.admin_note ?? "");
    setBillingReferenceId(request.billing_reference_id ?? "");
  }, [request]);

  async function handleSave(): Promise<void> {
    setIsSaving(true);
    try {
      await updateAdminServiceRequestServer({
        data: {
          requestId: request.id,
          payload: {
            status,
            admin_note: adminNote,
            billing_reference_id: billingReferenceId || null,
          },
        },
      });
      toast.success("服务申请已更新");
      await onSaved();
    } catch (error) {
      toast.error(extractApiErrorMessage(error, "更新服务申请失败"));
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold">后台处理</h2>
      <div className="grid gap-3 md:grid-cols-[220px_1fr]">
        <div className="space-y-2">
          <Label>状态</Label>
          <Select
            value={status}
            onValueChange={(value) => setStatus(value as ServiceRequestStatus)}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.values(ServiceRequestStatus).map((item) => (
                <SelectItem key={item} value={item}>
                  {SERVICE_STATUS_LABELS[item]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>计费引用</Label>
          <Input
            value={billingReferenceId}
            onChange={(event) => setBillingReferenceId(event.target.value)}
            placeholder="可选，后续对齐 Billing 后填入订单或账单引用"
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label>内部备注</Label>
        <Textarea
          value={adminNote}
          onChange={(event) => setAdminNote(event.target.value)}
          rows={3}
          placeholder="仅后台可见"
        />
      </div>
      <Button type="button" onClick={handleSave} disabled={isSaving}>
        保存处理信息
      </Button>
    </section>
  );
}

function ProgressPublisher({
  requestId,
  onPublished,
}: {
  requestId: string;
  onPublished: () => Promise<void>;
}) {
  const [updateType, setUpdateType] = useState<ServiceRequestUpdateType>(
    ServiceRequestUpdateType.progress,
  );
  const [nextStatus, setNextStatus] = useState<ServiceRequestStatus | "none">("none");
  const [title, setTitle] = useState("");
  const [contentText, setContentText] = useState("");
  const [isCustomerVisible, setIsCustomerVisible] = useState(true);
  const [assets, setAssets] = useState<ServiceRequestAssetResponse[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const assetIds = useMemo(() => assets.map((asset) => asset.id), [assets]);

  async function handleFiles(files: FileList | null): Promise<void> {
    if (!files || files.length === 0) return;
    setIsUploading(true);
    try {
      const uploaded: ServiceRequestAssetResponse[] = [];
      for (const file of Array.from(files)) {
        const response = (await createAdminServiceAssetSignatureServer({
          data: {
            requestId,
            payload: {
              asset_type: inferAssetType(file),
              filename: file.name,
              content_type: file.type || null,
              file_size: file.size,
            },
          },
        })) as {
          asset: ServiceRequestAssetResponse;
          signature: Parameters<typeof uploadToOSS>[1];
        };
        await uploadToOSS(file, response.signature);
        uploaded.push(response.asset);
      }
      setAssets((current) => [...current, ...uploaded]);
      toast.success("附件已上传");
    } catch (error) {
      toast.error(extractApiErrorMessage(error, "附件上传失败"));
    } finally {
      setIsUploading(false);
    }
  }

  async function handlePublish(): Promise<void> {
    if (!contentText.trim() && assetIds.length === 0) {
      toast.error("请填写进展内容或上传附件");
      return;
    }
    setIsPublishing(true);
    try {
      await createAdminServiceRequestUpdateServer({
        data: {
          requestId,
          payload: {
            update_type: updateType,
            title: title || null,
            content_text: contentText || null,
            content_blocks: [],
            asset_ids: assetIds,
            is_customer_visible: isCustomerVisible,
            next_status: nextStatus === "none" ? null : nextStatus,
          },
        },
      });
      setTitle("");
      setContentText("");
      setAssets([]);
      toast.success("服务进展已发布");
      await onPublished();
    } catch (error) {
      toast.error(extractApiErrorMessage(error, "发布服务进展失败"));
    } finally {
      setIsPublishing(false);
    }
  }

  return (
    <section className="space-y-3">
      <Separator />
      <h2 className="text-sm font-semibold">发布进展与交付物</h2>
      <div className="grid gap-3 md:grid-cols-3">
        <div className="space-y-2">
          <Label>类型</Label>
          <Select
            value={updateType}
            onValueChange={(value) => setUpdateType(value as ServiceRequestUpdateType)}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.values(ServiceRequestUpdateType).map((item) => (
                <SelectItem key={item} value={item}>
                  {SERVICE_UPDATE_TYPE_LABELS[item]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>同步状态</Label>
          <Select
            value={nextStatus}
            onValueChange={(value) => setNextStatus(value as ServiceRequestStatus | "none")}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">不变更</SelectItem>
              {Object.values(ServiceRequestStatus).map((item) => (
                <SelectItem key={item} value={item}>
                  {SERVICE_STATUS_LABELS[item]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <label className="flex items-end gap-2 pb-2 text-sm">
          <input
            type="checkbox"
            checked={isCustomerVisible}
            onChange={(event) => setIsCustomerVisible(event.target.checked)}
          />
          用户可见
        </label>
      </div>
      <div className="space-y-2">
        <Label>标题</Label>
        <Input value={title} onChange={(event) => setTitle(event.target.value)} />
      </div>
      <div className="space-y-2">
        <Label>内容</Label>
        <Textarea
          value={contentText}
          onChange={(event) => setContentText(event.target.value)}
          rows={5}
          placeholder="填写服务进展、问题说明或交付结论"
        />
      </div>
      <div className="space-y-2">
        <Label>图片、视频或文件</Label>
        <div className="flex flex-wrap items-center gap-3">
          <Button type="button" variant="outline" asChild disabled={isUploading}>
            <label>
              <Upload className="size-4" />
              上传附件
              <input
                type="file"
                multiple
                className="hidden"
                accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
                onChange={(event) => handleFiles(event.target.files)}
              />
            </label>
          </Button>
          {isUploading ? <span className="text-sm text-muted-foreground">上传中...</span> : null}
        </div>
        {assets.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {assets.map((asset) => (
              <Badge key={asset.id} variant="secondary">
                <Paperclip className="size-3" />
                {SERVICE_ASSET_TYPE_LABELS[asset.asset_type]} · {asset.filename}
              </Badge>
            ))}
          </div>
        ) : null}
      </div>
      <Button type="button" onClick={handlePublish} disabled={isPublishing || isUploading}>
        <Send className="size-4" />
        发布进展
      </Button>
    </section>
  );
}

function Timeline({
  timeline,
  isLoading,
}: {
  timeline: ServiceRequestUpdateResponse[];
  isLoading: boolean;
}) {
  return (
    <section className="space-y-3">
      <Separator />
      <h2 className="text-sm font-semibold">进展时间线</h2>
      {isLoading ? (
        <div className="text-sm text-muted-foreground">加载中...</div>
      ) : timeline.length === 0 ? (
        <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
          尚未发布进展。
        </div>
      ) : (
        <div className="space-y-3">
          {timeline.map((item) => (
            <article key={item.id} className="rounded-lg border p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{SERVICE_UPDATE_TYPE_LABELS[item.update_type]}</Badge>
                  <span className="font-medium">{item.title || "未命名进展"}</span>
                </div>
                <div className="text-xs text-muted-foreground">
                  {item.is_customer_visible ? "用户可见" : "内部可见"} ·{" "}
                  {formatDateTime(item.created_at)}
                </div>
              </div>
              {item.content_text ? (
                <p className="mt-3 whitespace-pre-wrap text-sm leading-6">{item.content_text}</p>
              ) : null}
              {item.assets && item.assets.length > 0 ? (
                <div className="mt-3 flex flex-wrap gap-2">
                  {item.assets.map((asset) => (
                    <a
                      key={asset.id}
                      href={asset.url || undefined}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-md border px-2.5 py-1 text-xs hover:bg-muted"
                    >
                      {SERVICE_ASSET_TYPE_LABELS[asset.asset_type]} ·{" "}
                      {asset.display_name || asset.filename}
                    </a>
                  ))}
                </div>
              ) : null}
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 truncate font-medium">{value}</div>
    </div>
  );
}

function inferAssetType(file: File): ServiceRequestAssetType {
  if (file.type.startsWith("image/")) return ServiceRequestAssetType.image;
  if (file.type.startsWith("video/")) return ServiceRequestAssetType.video;
  return ServiceRequestAssetType.file;
}

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}
